import { DocumentNode, gql, OperationVariables, QueryResult, useLazyQuery } from '@apollo/client';
import { NativeSelect } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getLocaleLabel, localeKeys } from '../../config/i18next';
import { ESPACE_PRIVE, EVENEMENTS, PAGE_PRIVEES, PAGE_PUBLIQUES, ROUTE_MODEL_MAP } from '../../constants/routing';
import { StrapiLocalizableEntity } from '../../models/layout';
import { ModelName } from '../../models/routes';
import { getRouteKeyFromSlug, getRouteSlug, RouteKey, translateRoutePath } from '../../utils/routes';
import { IconChevron } from '../icons/IconChevron';

type LanguageSelectorProps = { locale: string };
type Params = { slug: string };

type LanguageSelectorGqlMap = { [key: string]: DocumentNode };

type CollectionSlugQuery = {
  briqueSavoirs?: StrapiLocalizableEntity[];
  parcoursFormations?: StrapiLocalizableEntity[];
  quizs?: StrapiLocalizableEntity[];
  pagePubliques?: StrapiLocalizableEntity[];
  pagePrivees?: StrapiLocalizableEntity[];
  evenements?: StrapiLocalizableEntity[];
};

/**
 * Resolves the slug of a Strapi entity in the target locale using the localizations
 * graph returned by GraphQL. Returns undefined if no localized version exists,
 * so the caller can redirect to the "not available" page instead.
 * @param modelName
 * @param newlocale
 * @param query
 * @returns
 */
const translateSlug = (
  modelName: ModelName,
  newlocale: string,
  query: QueryResult<CollectionSlugQuery, OperationVariables>
) => {
  const actualCollection = query?.data?.[modelName]?.[0];
  const associatedCollections = actualCollection?.localizations ?? [];
  const translatedCollection = associatedCollections.find((col) => col.locale === newlocale);
  return translatedCollection?.Slug;
};

/**
 * Determines the Strapi collection model name for the current page so the correct
 * GraphQL query is used when fetching the translated slug.
 * Uses getRouteKeyFromSlug to reverse-translate the URL segment back to a neutral
 * RouteKey, keeping ROUTE_MODEL_MAP locale-independent.
 * @param pathArray
 * @param isPublic
 * @param locale
 * @returns
 */
const getTypeOfContent = (pathArray: string[], isPublic: boolean, locale: string): ModelName => {
  if (isPublic) return ROUTE_MODEL_MAP[PAGE_PUBLIQUES];
  const routeKey = getRouteKeyFromSlug(locale, pathArray[1]);
  // Return the ModelName for events — RouteKey and ModelName share the same 'evenements' value
  if (routeKey === EVENEMENTS) return ROUTE_MODEL_MAP[EVENEMENTS];
  if (routeKey && ROUTE_MODEL_MAP[routeKey]) return ROUTE_MODEL_MAP[routeKey];
  return ROUTE_MODEL_MAP[PAGE_PRIVEES];
};

export const LanguageSelector = ({ locale }: LanguageSelectorProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams<Params>() ?? '';
  const pathArray = location.pathname.split('/').slice(2);
  const isPublic = pathArray[0] !== getRouteSlug(locale, ESPACE_PRIVE);
  const type = getTypeOfContent(pathArray, isPublic, locale);
  const queryGql = LANGUAGE_SELECTOR_QUERIES[type];
  const [loadSlugs] = useLazyQuery<CollectionSlugQuery>(queryGql);

  /**
   * Handles language switching. Two paths:
   * - No content slug or événements: translate the URL segments and navigate directly
   *   (no GraphQL fetch needed, as these pages do not have per-content slugs).
   * - With content slug: fetch the localized slug from Strapi, then navigate to it.
   *   Falls back to the "not available" page if the content has no version in the target locale.
   */
  const switchLanguage = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newlocale = event.target.value.toLowerCase();
      if (!slug || type === EVENEMENTS) {
        navigate(`/${newlocale}/${translateRoutePath(locale, newlocale, pathArray.join('/'))}`);
        return;
      }

      loadSlugs({
        variables: { locale, slug },
        context: { isPublic: isPublic }
      })
        .then((query) => {
          const newSlug = translateSlug(type, newlocale, query);
          let newPath;
          if (newSlug) {
            newPath = isPublic ? newSlug : `${getRouteSlug(newlocale, ESPACE_PRIVE)}/${newSlug}`;
          } else {
            newPath = getRouteSlug(newlocale, 'non-disponible' as RouteKey);
          }
          navigate(`/${newlocale}/${newPath}`);
        })
        .catch((err) => {
          console.error('switchLanguage: %o', err);
          navigate(`/${newlocale}/${getRouteSlug(newlocale, 'non-disponible' as RouteKey)}`);
        });
    },
    [slug, pathArray, navigate, loadSlugs, locale, isPublic, type]
  );
  return (
    <NativeSelect
      aria-label={t('header.languageSelector') ?? ''}
      IconComponent={IconChevron}
      value={locale.toUpperCase()}
      onChange={switchLanguage}
      sx={{
        color: 'primary.main',
        fontWeight: 600,
        '&:before, &:focus:before': {
          display: 'none!important'
        },
        '&:hover': {
          color: 'text.primary'
        },
        'select.MuiNativeSelect-select.MuiInput-input': {
          letterSpacing: '1px',
          pr: '1.75rem'
        },
        svg: {
          position: 'absolute',
          top: 0,
          right: 0,
          pointerEvents: 'none',
          height: '100%',
          width: '1.5rem'
        }
      }}
    >
      {localeKeys.map((lang) => (
        <option
          key={lang}
          lang={lang}
          aria-label={getLocaleLabel(lang.toLowerCase())}
          aria-current={lang.toLowerCase() === locale ? 'true' : undefined}
        >
          {lang.toUpperCase()}
        </option>
      ))}
    </NativeSelect>
  );
};

const LANGUAGE_SELECTOR_QUERIES: LanguageSelectorGqlMap = {};

for (const modelName of Object.values(ROUTE_MODEL_MAP)) {
  LANGUAGE_SELECTOR_QUERIES[modelName] = gql`
    query GetLanguageSelector${modelName}($locale: I18NLocaleCode!, $slug: String!) {
      ${modelName}(locale: $locale, filters: { Slug: { eq: $slug } }) {
        Slug
        locale
        localizations {
          locale
          Slug
        }
      }
    }
  `;
}
